import { tokens } from "../../../../public/tokens";

/**
 * AP-1219 - Year is hardcoded 1-4, mirroring `ModuleDetail.js`'s `ModuleYear` list, because no
 * programme/version duration field exists to derive it from. Semester/Term is derived from the
 * programme's own `Term_Period`. Neither is confirmed by product yet — see
 * docs/AP-1219/design-fallback-notes.md items 2 and 3.
 */
export const YEAR_OPTIONS = [1, 2, 3, 4].map((year) => ({ value: String(year), label: String(year) }));

/**
 * Static 1-4 term list for the Assign Modules modal's Term filter — unrelated to
 * `getSemesterTermOptions` below (that one derives a programme's actual Semester/Term options
 * from its `Term_Period`). Numeric `value`s (unlike `YEAR_OPTIONS`'s stringified ones) so the
 * multi-select posts `term` as an array of numbers.
 */
export const TERM_OPTIONS = [1, 2, 3, 4].map((term) => ({ value: term, label: `Term ${term}` }));

/**
 * `Programme_Modules.module_status` values, colored from tokens.js's closest hue to the legacy
 * `RenderModuleStatus` (front/src/utils/CommonGroupingItem.js) colors, since Figma doesn't
 * specify this filter's open-state design.
 */
export const MODULE_STATUS_OPTIONS = [
    { value: "Draft", label: "Draft", color: tokens.colors.states.alert.default },
    { value: "New", label: "New", color: tokens.colors.brand["tide-blue"]["500"] },
    { value: "Final", label: "Final", color: tokens.colors.states.success.default },
    { value: "Approved", label: "Approved", color: tokens.colors.brand["karoo-gold"]["500"] },
    { value: "Inactive", label: "Inactive", color: tokens.colors.brand.blue["500"] },
];

const TERM_PERIOD_SEMESTER_TERM_VALUES = {
    "Year (1 Term)": [1],
    "Semester (2 Terms)": [1, 2],
    "Trimester (3 Terms)": [1, 2, 3],
};

/** @param {string} termPeriod Programme's `Term_Period` value. */
export function getSemesterTermOptions(termPeriod) {
    const values = TERM_PERIOD_SEMESTER_TERM_VALUES[termPeriod] || TERM_PERIOD_SEMESTER_TERM_VALUES["Semester (2 Terms)"];
    return values.map((value) => ({ value: String(value), label: String(value) }));
}

// Display labels follow the Figma design ("Foundation"/"Elective"), not legacy "Foundational"/"Specialised" wording.
export const MODULE_GROUPS = [
    {
        type: 1,
        key: "core",
        label: "Core Modules",
        creditsField: "core_credits",
        suppliedField: "core_supplied_credits",
        relationField: "core_module",
    },
    {
        type: 2,
        key: "foundation",
        label: "Foundation Modules",
        creditsField: "foundational_credits",
        suppliedField: "foundational_supplied_credits",
        relationField: "foundation_module",
    },
    {
        type: 3,
        key: "elective",
        label: "Elective Modules",
        creditsField: "elective_credits",
        suppliedField: "elective_supplied_credits",
        relationField: "elective_module",
    },
];

export const PHASE_EXPORT_HEADERS = [
    "No.",
    "Name",
    ...MODULE_GROUPS.flatMap((group) => {
        const groupName = group.label.replace(" Modules", "");
        return [`${groupName} Modules`, `${groupName} Credits`];
    }),
];

/**
 * Builds Study Phases export rows (the Export dropdown on the list/re-arrange screens) from the
 * already-loaded phase list, joining each group's module names alongside its credit total.
 * @param {Array} phases Phase objects from `useStudyPathPhaseList`.
 */
export function buildPhaseExportRows(phases) {
    return phases.map((phase) => ({
        "No.": phase.order_number ?? "-",
        "Name": phase.name ?? "-",
        ...MODULE_GROUPS.reduce((groupColumns, group) => {
            const groupName = group.label.replace(" Modules", "");
            const modules = phase[group.relationField] || [];
            groupColumns[`${groupName} Modules`] = modules.length
                ? modules.map((module) => module.name).join(", ")
                : "-";
            groupColumns[`${groupName} Credits`] = phase[group.creditsField] ?? "-";
            return groupColumns;
        }, {}),
    }));
}

/**
 * Programme_Modules.type stores the legacy string "specialised" for the group the rest of this feature calls "elective" (see docs/AP-1219/02-figma-and-legacy-code-map.md's naming mismatch).
 */
const MODULE_TYPE_STRING_TO_GROUP_KEY = {
    core: "core",
    foundation: "foundation",
    specialised: "elective",
};

/** @param {string} moduleTypeString `Programme_Modules.type` value ("core"/"foundation"/"specialised"). */
export function getGroupForModuleType(moduleTypeString) {
    const groupKey = MODULE_TYPE_STRING_TO_GROUP_KEY[moduleTypeString];
    return MODULE_GROUPS.find((candidate) => candidate.key === groupKey);
}

/** @param {string} moduleTypeString `Programme_Modules.type` value ("core"/"foundation"/"specialised"). */
export function getGroupLabelForModuleType(moduleTypeString) {
    const group = getGroupForModuleType(moduleTypeString);
    return group ? group.label : moduleTypeString;
}

/**
 * Reverse of `getGroupForModuleType` — the legacy `Programme_Modules.type` string for a `MODULE_GROUPS` key.
 * @param {string} groupKey `MODULE_GROUPS` entry key ("core"/"foundation"/"elective").
 */
export function getModuleTypeStringForGroup(groupKey) {
    return Object.keys(MODULE_TYPE_STRING_TO_GROUP_KEY).find((typeString) => MODULE_TYPE_STRING_TO_GROUP_KEY[typeString] === groupKey);
}

/**
 * A phase's "Unassigned Modules" panel should reflect staged, not-yet-saved assign/unassign edits,
 * not just the server's own unassigned-modules list — a module unstaged from `hydrateFrom` (its
 * pre-edit baseline) but not restaged into any group belongs back in view immediately.
 * @param {Array} unassignedModules Server truth: modules assigned to no phase.
 * @param {object} stagedModules `{ [group.key]: module[] }` current staged state.
 * @param {object|null} hydrateFrom Same shape, the baseline this phase's modules hydrated from.
 * @param {(module: object) => (string|number)} getModuleId
 */
export function mergeVisibleUnassignedModules(unassignedModules, stagedModules, hydrateFrom, getModuleId) {
    const stagedModuleIds = MODULE_GROUPS.flatMap((group) => stagedModules[group.key].map(getModuleId));

    const locallyUnassignedModules = MODULE_GROUPS.flatMap((group) => (
        (hydrateFrom?.[group.key] ?? [])
            .filter((module) => !stagedModuleIds.includes(getModuleId(module)))
            .map((module) => ({ ...module, type: getModuleTypeStringForGroup(group.key) }))
    ));

    const unassignedIds = new Set(unassignedModules.map(getModuleId));

    return [
        ...unassignedModules.filter((module) => !stagedModuleIds.includes(getModuleId(module))),
        ...locallyUnassignedModules.filter((module) => !unassignedIds.has(getModuleId(module))),
    ];
}

/**
 * The phase list and the phase form source module rows from different endpoints with different
 * field casing (Id/Code/Duration vs id/code/duration) — normalize once rather than checking both
 * casings at every render site.
 * @param {Array} modules Raw module rows from either endpoint.
 */
export function normalizeModuleRows(modules) {
    return modules.map((module) => ({
        id: module.id ?? module.Id ?? module.module_id,
        name: module.name ?? module.portal,
        code: module.code ?? module.Code,
        duration: module.duration ?? module.Duration,
        credits: module.credits,
        hasWarning: module.hasWarning,
    }));
}

/**
 * Resolves a module row's real module id regardless of source shape: a phase-relation row (e.g.
 * `StudyPhasesList.js`'s `selectedPhase.core_module`/etc.) carries both `Id` (the
 * `sp_assigned_module_pmc_study_path` row's own PK) and `module_id` (the real module id) —
 * `module_id` must win. A freshly-staged row (from the Unassigned Modules panel) has no
 * `module_id` field at all, only `Id`, which already is the real module id there.
 * @param {object} module Module row from either source shape.
 */
export function getStudyPathModuleId(module) {
    return module.module_id ?? module.Id;
}

/** @param {Array} modules Rows carrying a `credits` field. */
export function sumModuleCredits(modules) {
    return modules.reduce((total, module) => total + Number(module.credits || 0), 0);
}

/**
 * Ready/warning/attention tone for a group or phase's credit result. Returns `null` (no badge)
 * only when a group is truly empty (no target, nothing supplied yet either) — matching the
 * "Foundation Modules" state in the SD-3 design; a group with modules assigned but no target set
 * gets the "attention" tone instead (SD-3's "Specialised Modules" state).
 * @param {number} required Stored required-credits target.
 * @param {number} supplied Live-computed supplied credits, shown as "Maximum Credits". Badges
 *   render these as `required/supplied`, so the tone is "supplied covers required".
 */
export function getCreditTone(required, supplied) {
    if (!(Number(required) > 0)) {
        return Number(supplied) > 0 ? "attention" : null;
    }
    return Number(supplied) >= Number(required) ? "success" : "warning";
}

/**
 * Return the icon name for a group or phase's credit result tone.
 * @param {string|null} tone Return value of `getCreditTone`.
 */
export function getCreditIcon(tone) {
    if (tone === "success") {
        return "check-circle";
    }
    if (tone === "attention") {
        return "info-circle";
    }
    return "exclamation-triangle";
}

export const STANDARD_ROWS_PER_PAGE_SIZES = [10, 15, 25, 30, 50, 100];

/**
 * Trims the standard rows-per-page tiers to what's useful for a given total: every tier at or
 * below `total`, plus the one tier just above it (so a 14-item list offers 10/15, not the full
 * 10/15/25/30/50/100).
 * @param {number} total Total row count being paginated.
 * @param {number[]} [sizes] Candidate tier sizes to trim from; defaults to `STANDARD_ROWS_PER_PAGE_SIZES`.
 */
export function getRowsPerPageOptions(total, sizes = STANDARD_ROWS_PER_PAGE_SIZES) {
    if (!total || total <= 0) {
        return [sizes[0]];
    }
    const included = sizes.filter((size) => size <= total);
    const nextTier = sizes.find((size) => size > total);
    return nextTier ? [...included, nextTier] : included;
}
